import {
  BAM,
  BamOptions,
  isRefreshableToken,
  Jwt,
  RefreshableJwt
} from '@bam/sdk';
import localforage from 'localforage';
import account from '../api/account';
import { parseJwt } from '~/utils/utils';

export default async function ({ app, $auth, $config }, inject) {
  $auth.onRedirect((to, _from) => {
    return app.localePath(to);
  });

  let localStore;
  if (!process.server) {
    localStore = localforage.createInstance({
      // List of drivers used
      driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
      // Prefix all storage keys to prevent conflicts
      name: 'BAM-cache'
    });
  }

  /**
   * Creating and injecting the BAM SDK for subsequent use
   */
  const sdkKey = 'BAM_SDK';
  let bamApi: BAM;

  const sdkState = await localStore?.getItem(sdkKey);
  if (sdkState != null) {
    bamApi = await BAM.build(sdkState as BamOptions);
  } else {
    const baseUrl = `https://${$config.apiBaseUrl}`;
    bamApi = await BAM.build({
      baseUrl
    });
  }

  function authorizationChangedListener($auth, bamApi: BAM) {
    return async (data: { jwt: Jwt | RefreshableJwt }) => {
      $auth.strategy.token.set(data.jwt?.token);
      if (isRefreshableToken(data.jwt)) {
        $auth.strategy.refreshToken.set(data.jwt?.refreshToken);
      }

      const tokenInfo = parseJwt(data.jwt?.token);
      if (tokenInfo.id != null) {
        const userData = await bamApi.account.getUser({ id: tokenInfo.id });
        const userPayload = {
          ...userData,
          ...tokenInfo
        };
        await $auth.setUser(userPayload);
      } else if (tokenInfo.guestId != null) {
        // Guest token
        await $auth.setUser(tokenInfo);
      }

      // Update the SDK state
      await localStore.setItem(sdkKey, bamApi.serialize());
    };
  }

  function logoutListener($auth) {
    return () => {
      $auth.logout();
      // Update the SDK state
      return localStore.removeItem(sdkKey);
    };
  }

  function organizerChangedListener(bamApi: BAM) {
    return (_data: { organizerName: string }) => {
      // Update the saved SDK state
      return localStore.setItem(sdkKey, bamApi.serialize());
    };
  }

  // If we do not update the SDK state on the refresh, 
  // a regular user will be calling the token refresh on every call after the first expiry
  function refreshTokenListener(bamApi: BAM) {
    return (_data: { jwt: Jwt | RefreshableJwt }) => {
      return localStore.setItem(sdkKey, bamApi.serialize());
    };
  }

  bamApi.eventEmitter.on(
    'authorizationChanged',
    authorizationChangedListener($auth, bamApi)
  );
  bamApi.eventEmitter.on('logout', logoutListener($auth));
  bamApi.eventEmitter.on('organizerChanged', organizerChangedListener(bamApi));
  bamApi.eventEmitter.on('tokenRefreshed', refreshTokenListener(bamApi));

  bamApi.client.defaults.headers['Cache-Control'] = 'no-cache';
  bamApi.tenantClient.defaults.headers['Cache-Control'] = 'no-cache';

  const services = {
    account: account(bamApi)
  };

  inject('bamApi', bamApi);
  inject('isProduction', $config.env === 'production');
  inject('showLogin', $config.features.showLogin);
  inject('showCrypto', $config.features.showCrypto);
  inject('defaultPaymentProvider', $config.features.defaultPaymentProvider);
  inject('showTickets', $config.features.showTickets);
  inject('showSecondary', $config.features.showSecondary);
  inject('mainOrganizer', $config.features.mainOrganizer);
  inject('enableOrganizers', $config.features.enableOrganizers);
  inject('requirePhoneVerification', $config.features.requirePhoneVerification);
  inject('enableMetaTracking', $config.features.enableMetaTracking);
  inject('metaPixelId', $config.features.metaPixelId);
  inject('showReportsDashboard', $config.features.showReportsDashboard);
  inject('showNftCollectibles', $config.features.showNftCollectibles);
  inject('featuredEvents', $config.features.featuredEvents);
  inject('pdfTemplates', $config.features.pdfTemplates);
  inject(
    'showOrganizerReportsDashboard',
    $config.features.showOrganizerReportsDashboard
  );
  inject('services', services);
  inject('maximalRequestBodyLength', $config.maximalRequestBodyLength);
}
